import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmt } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const GP_CLS = {
  star: "border-emerald-600 bg-emerald-50 text-emerald-700",
  plowhorse: "border-blue-600 bg-blue-50 text-blue-700",
  puzzle: "border-amber-500 bg-amber-50 text-amber-700",
  dog: "border-red-500 bg-red-50 text-red-700",
};

const parseIngredients = (text) => text.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
  const [name, qty, unit, cost] = l.split("|").map((s) => (s || "").trim());
  return { name, qty: Number(qty || 0), unit: unit || "unit", cost_per_unit: Number(cost || 0) };
});

export default function Toolkit() {
  const [tab, setTab] = useState("recipes");
  const [recipes, setRecipes] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [engineering, setEngineering] = useState([]);
  const [recipeOpen, setRecipeOpen] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState(null);
  const [rForm, setRForm] = useState({ name: "", portions: 10, target_gp_pct: 70, ingredients_text: "" });
  const [supOpen, setSupOpen] = useState(false);
  const [editingSup, setEditingSup] = useState(null);
  const [sForm, setSForm] = useState({ name: "", contact_name: "", email: "", phone: "", supplies: "", notes: "" });
  const [gp, setGp] = useState({ cost: "", price: "" });

  const loadRecipes = () => api.get("/recipes").then((r) => setRecipes(r.data)).catch((e) => toast.error(formatApiError(e)));
  const loadSuppliers = () => api.get("/suppliers").then((r) => setSuppliers(r.data)).catch((e) => toast.error(formatApiError(e)));
  const loadEngineering = () => api.get("/toolkit/menu-engineering").then((r) => setEngineering(r.data.items)).catch(() => {});
  useEffect(() => { loadRecipes(); loadSuppliers(); loadEngineering(); }, []);

  const saveRecipe = async (e) => {
    e.preventDefault();
    const payload = { name: rForm.name, portions: Number(rForm.portions), target_gp_pct: Number(rForm.target_gp_pct), ingredients: parseIngredients(rForm.ingredients_text) };
    try {
      if (editingRecipe) await api.patch(`/recipes/${editingRecipe.id}`, payload);
      else await api.post("/recipes", payload);
      toast.success(editingRecipe ? "Recipe updated" : "Recipe added");
      setRecipeOpen(false);
      setEditingRecipe(null);
      setRForm({ name: "", portions: 10, target_gp_pct: 70, ingredients_text: "" });
      loadRecipes();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteRecipe = async (r) => {
    if (!window.confirm(`Delete recipe "${r.name}"?`)) return;
    try { await api.delete(`/recipes/${r.id}`); loadRecipes(); } catch (err) { toast.error(formatApiError(err)); }
  };

  const saveSupplier = async (e) => {
    e.preventDefault();
    try {
      if (editingSup) await api.patch(`/suppliers/${editingSup.id}`, sForm);
      else await api.post("/suppliers", sForm);
      toast.success(editingSup ? "Supplier updated" : "Supplier added");
      setSupOpen(false);
      setEditingSup(null);
      setSForm({ name: "", contact_name: "", email: "", phone: "", supplies: "", notes: "" });
      loadSuppliers();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteSupplier = async (s) => {
    if (!window.confirm(`Delete supplier "${s.name}"?`)) return;
    try { await api.delete(`/suppliers/${s.id}`); loadSuppliers(); } catch (err) { toast.error(formatApiError(err)); }
  };

  const cost = Number(gp.cost) || 0;
  const price = Number(gp.price) || 0;
  const gpPct = price > 0 ? ((price - cost) / price) * 100 : 0;
  const markup = cost > 0 ? ((price - cost) / cost) * 100 : 0;

  return (
    <div data-testid="toolkit-page" className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">F&amp;B Toolkit</h1>
        <p className="mt-1 text-sm text-muted-foreground">Recipe costing, gross profit, menu engineering and supplier management.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="w-full justify-start overflow-x-auto bg-muted" data-testid="toolkit-tabs">
          <TabsTrigger value="recipes" data-testid="toolkit-tab-recipes">Recipe Costing</TabsTrigger>
          <TabsTrigger value="gp" data-testid="toolkit-tab-gp">GP Calculator</TabsTrigger>
          <TabsTrigger value="engineering" data-testid="toolkit-tab-engineering">Menu Engineering</TabsTrigger>
          <TabsTrigger value="suppliers" data-testid="toolkit-tab-suppliers">Suppliers</TabsTrigger>
        </TabsList>

        <TabsContent value="recipes" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Button data-testid="new-recipe-button" onClick={() => { setEditingRecipe(null); setRForm({ name: "", portions: 10, target_gp_pct: 70, ingredients_text: "" }); setRecipeOpen(true); }} className="rounded-md bg-ink hover:bg-ink-light">
              <Plus className="mr-2 h-4 w-4" /> New Recipe
            </Button>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {recipes.map((r) => (
              <div key={r.id} data-testid={`recipe-card-${r.id}`} className="border border-border bg-white p-5">
                <div className="flex items-start justify-between">
                  <h3 className="font-heading text-base font-bold">{r.name}</h3>
                  <span className="text-xs text-muted-foreground tabular-nums">{r.portions} portions</span>
                </div>
                <dl className="mt-3 space-y-1.5 text-sm">
                  <div className="flex justify-between"><dt className="text-muted-foreground">Batch cost</dt><dd className="tabular-nums">{fmt(r.total_cost)}</dd></div>
                  <div className="flex justify-between"><dt className="text-muted-foreground">Cost / portion</dt><dd className="font-semibold tabular-nums" data-testid={`recipe-portion-cost-${r.id}`}>{fmt(r.cost_per_portion)}</dd></div>
                  <div className="flex justify-between border-t border-border pt-1.5">
                    <dt className="font-medium">Suggested price @ {r.target_gp_pct}% GP</dt>
                    <dd className="font-bold text-brand tabular-nums" data-testid={`recipe-suggested-${r.id}`}>{fmt(r.suggested_price)}</dd>
                  </div>
                </dl>
                <div className="mt-3 flex gap-2 border-t border-border pt-3">
                  <Button data-testid={`edit-recipe-${r.id}`} size="sm" variant="outline" className="rounded-md" onClick={() => { setEditingRecipe(r); setRForm({ name: r.name, portions: r.portions, target_gp_pct: r.target_gp_pct, ingredients_text: (r.ingredients || []).map((i) => `${i.name} | ${i.qty} | ${i.unit} | ${i.cost_per_unit}`).join("\n") }); setRecipeOpen(true); }}>Edit</Button>
                  <Button data-testid={`delete-recipe-${r.id}`} size="sm" variant="outline" className="rounded-md border-red-300 text-red-600 hover:bg-red-50" onClick={() => deleteRecipe(r)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            ))}
            {recipes.length === 0 && <p className="col-span-3 border border-border bg-white px-5 py-10 text-center text-sm text-muted-foreground">No recipes yet — cost your first dish.</p>}
          </div>
        </TabsContent>

        <TabsContent value="gp" className="mt-4">
          <div className="max-w-lg border border-border bg-white p-6" data-testid="gp-calculator">
            <h2 className="font-heading text-sm font-bold uppercase tracking-[0.15em]">Gross profit calculator</h2>
            <div className="mt-4 grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Cost (£)</Label><Input data-testid="gp-cost-input" type="number" step="0.01" min="0" value={gp.cost} onChange={(e) => setGp({ ...gp, cost: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Sell price (£)</Label><Input data-testid="gp-price-input" type="number" step="0.01" min="0" value={gp.price} onChange={(e) => setGp({ ...gp, price: e.target.value })} /></div>
            </div>
            <dl className="mt-5 space-y-2 border-t border-border pt-4 text-sm">
              <div className="flex justify-between"><dt className="text-muted-foreground">Cash margin</dt><dd className="font-semibold tabular-nums" data-testid="gp-margin">{fmt(price - cost)}</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Gross profit</dt><dd className="font-heading text-lg font-black text-brand tabular-nums" data-testid="gp-percent">{gpPct.toFixed(1)}%</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Markup on cost</dt><dd className="tabular-nums" data-testid="gp-markup">{markup.toFixed(1)}%</dd></div>
              <div className="flex justify-between border-t border-border pt-2"><dt className="text-muted-foreground">Price needed for 70% GP</dt><dd className="font-semibold tabular-nums" data-testid="gp-target">{cost > 0 ? fmt(cost / 0.3) : "—"}</dd></div>
            </dl>
          </div>
        </TabsContent>

        <TabsContent value="engineering" className="mt-4">
          <div className="border border-border bg-white" data-testid="menu-engineering">
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left">
                  {["Menu", "Price/head", "Cost/head", "GP %", "Times ordered", "Classification"].map((h) => (
                    <th key={h} className="px-5 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {engineering.map((m) => (
                  <tr key={m.id} data-testid={`engineering-row-${m.id}`} className="border-b border-border last:border-0">
                    <td className="px-5 py-3 font-medium">{m.name}</td>
                    <td className="px-5 py-3 tabular-nums">{fmt(m.price_per_head)}</td>
                    <td className="px-5 py-3 tabular-nums">{m.cost_per_head ? fmt(m.cost_per_head) : "—"}</td>
                    <td className="px-5 py-3 tabular-nums">{m.gp_pct != null ? `${m.gp_pct}%` : "—"}</td>
                    <td className="px-5 py-3 tabular-nums">{m.orders}</td>
                    <td className="px-5 py-3">
                      {m.classification ? (
                        <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-[0.15em] ${GP_CLS[m.classification]}`} data-testid={`engineering-class-${m.id}`}>{m.classification}</span>
                      ) : (
                        <span className="text-xs text-muted-foreground" data-testid={`engineering-class-${m.id}`}>— set cost</span>
                      )}
                    </td>
                  </tr>
                ))}
                {engineering.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-muted-foreground">Add cost per head to your menus to unlock menu engineering.</td></tr>}
              </tbody>
            </table>
            </div>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">Stars: high profit &amp; popularity · Plowhorses: popular, low margin · Puzzles: high margin, rarely ordered · Dogs: rework or retire. Set cost/head on the Menus page.</p>
        </TabsContent>

        <TabsContent value="suppliers" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Button data-testid="new-supplier-button" onClick={() => { setEditingSup(null); setSForm({ name: "", contact_name: "", email: "", phone: "", supplies: "", notes: "" }); setSupOpen(true); }} className="rounded-md bg-ink hover:bg-ink-light">
              <Plus className="mr-2 h-4 w-4" /> Add Supplier
            </Button>
          </div>
          <div className="border border-border bg-white">
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left">
                  {["Supplier", "Supplies", "Contact", "Notes", ""].map((h) => (
                    <th key={h} className="px-5 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {suppliers.map((s) => (
                  <tr key={s.id} data-testid={`supplier-row-${s.id}`} className="border-b border-border last:border-0">
                    <td className="px-5 py-3 font-medium">{s.name}</td>
                    <td className="px-5 py-3 text-muted-foreground">{s.supplies}</td>
                    <td className="px-5 py-3">{s.contact_name}{s.phone ? ` · ${s.phone}` : ""}</td>
                    <td className="px-5 py-3 text-muted-foreground">{s.notes}</td>
                    <td className="px-5 py-3">
                      <div className="flex gap-2">
                        <Button data-testid={`edit-supplier-${s.id}`} size="sm" variant="outline" className="rounded-md" onClick={() => { setEditingSup(s); setSForm({ name: s.name, contact_name: s.contact_name || "", email: s.email || "", phone: s.phone || "", supplies: s.supplies || "", notes: s.notes || "" }); setSupOpen(true); }}>Edit</Button>
                        <Button data-testid={`delete-supplier-${s.id}`} size="sm" variant="outline" className="rounded-md border-red-300 text-red-600 hover:bg-red-50" onClick={() => deleteSupplier(s)}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {suppliers.length === 0 && <tr><td colSpan={5} className="px-5 py-10 text-center text-muted-foreground">No suppliers yet.</td></tr>}
              </tbody>
            </table>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={recipeOpen} onOpenChange={setRecipeOpen}>
        <DialogContent className="max-w-md bg-white" data-testid="recipe-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editingRecipe ? "Edit Recipe" : "New Recipe"}</DialogTitle></DialogHeader>
          <form onSubmit={saveRecipe} className="space-y-4">
            <div className="space-y-1.5"><Label>Recipe name</Label><Input data-testid="recipe-name-input" required value={rForm.name} onChange={(e) => setRForm({ ...rForm, name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Portions</Label><Input data-testid="recipe-portions-input" type="number" min="1" value={rForm.portions} onChange={(e) => setRForm({ ...rForm, portions: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Target GP %</Label><Input data-testid="recipe-gp-input" type="number" min="1" max="95" value={rForm.target_gp_pct} onChange={(e) => setRForm({ ...rForm, target_gp_pct: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5">
              <Label>Ingredients — one per line: Name | qty | unit | cost per unit</Label>
              <textarea data-testid="recipe-ingredients-input" rows={5} placeholder={"Brisket | 8 | kg | 9.20\nBrioche baps | 50 | each | 0.42"} value={rForm.ingredients_text} onChange={(e) => setRForm({ ...rForm, ingredients_text: e.target.value })} className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm" />
            </div>
            <Button data-testid="recipe-submit-button" type="submit" className="w-full rounded-md bg-ink hover:bg-ink-light">{editingRecipe ? "Save Changes" : "Cost Recipe"}</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={supOpen} onOpenChange={setSupOpen}>
        <DialogContent className="max-w-md bg-white" data-testid="supplier-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editingSup ? "Edit Supplier" : "Add Supplier"}</DialogTitle></DialogHeader>
          <form onSubmit={saveSupplier} className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5"><Label>Name</Label><Input data-testid="supplier-name-input" required value={sForm.name} onChange={(e) => setSForm({ ...sForm, name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Contact</Label><Input data-testid="supplier-contact-input" value={sForm.contact_name} onChange={(e) => setSForm({ ...sForm, contact_name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Phone</Label><Input data-testid="supplier-phone-input" value={sForm.phone} onChange={(e) => setSForm({ ...sForm, phone: e.target.value })} /></div>
            <div className="col-span-2 space-y-1.5"><Label>Email</Label><Input data-testid="supplier-email-input" value={sForm.email} onChange={(e) => setSForm({ ...sForm, email: e.target.value })} /></div>
            <div className="col-span-2 space-y-1.5"><Label>What they supply</Label><Input data-testid="supplier-supplies-input" placeholder="Meat, dairy, dry goods…" value={sForm.supplies} onChange={(e) => setSForm({ ...sForm, supplies: e.target.value })} /></div>
            <div className="col-span-2 space-y-1.5"><Label>Notes</Label><Input data-testid="supplier-notes-input" value={sForm.notes} onChange={(e) => setSForm({ ...sForm, notes: e.target.value })} /></div>
            <div className="col-span-2"><Button data-testid="supplier-submit-button" type="submit" className="w-full rounded-md bg-ink hover:bg-ink-light">{editingSup ? "Save Changes" : "Add Supplier"}</Button></div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
